import { useState, type ReactNode } from 'react';
import styles from './DocInlineChips.module.css';
import { cx } from '../../lib/cx';

export interface DocInlineChipItem {
  id: string;
  label: ReactNode;
  checked: boolean;
}

export interface DocInlineChipsProps {
  items: DocInlineChipItem[];
  onToggle: (id: string) => void;
  /** Pokud dodáno, zobrazí se mini-input pro přidání vlastního chipu (skrytý při tisku). */
  onAdd?: (label: string) => void;
  /** Pokud dodáno, u nefixovaných chipů se zobrazí ✕ (skryté při tisku). */
  onRemove?: (id: string) => void;
  /** ID chipů, které nelze odebrat. */
  fixedIds?: string[];
}

/**
 * Chip selektor uvnitř buňky DocRow. Zobrazené chipy = trvalá hodnota dokumentu;
 * checkbox box, mini-input a ✕ tlačítka jsou interaktivní pomůcky a v PDF zmizí.
 */
export function DocInlineChips({
  items,
  onToggle,
  onAdd,
  onRemove,
  fixedIds,
}: DocInlineChipsProps) {
  const [draft, setDraft] = useState('');

  function submit() {
    const v = draft.trim();
    if (!v || !onAdd) return;
    onAdd(v);
    setDraft('');
  }

  return (
    <div className={styles.row}>
      {items.map((item) => {
        const fixed = fixedIds?.includes(item.id);
        return (
          <span key={item.id} className={styles.wrap}>
            <button
              type="button"
              className={cx(styles.chip, item.checked && styles.on, !item.checked && 'docNoPrint')}
              onClick={() => onToggle(item.id)}
              aria-pressed={item.checked}
            >
              <span className={cx(styles.box, 'docNoPrint')} aria-hidden>
                {item.checked ? '✓' : ''}
              </span>
              <span className={styles.checkmarkPrint} aria-hidden>✓</span>
              {item.label}
            </button>
            {onRemove && !fixed && (
              <button
                type="button"
                className={cx(styles.remove, 'docNoPrint')}
                onClick={() => onRemove(item.id)}
                aria-label="Odebrat"
                title="Odebrat položku"
              >
                ✕
              </button>
            )}
          </span>
        );
      })}
      {onAdd && (
        <span className={cx(styles.add, 'docNoPrint')}>
          <input
            type="text"
            className={styles.addInput}
            placeholder="+ přidat"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                submit();
              }
            }}
          />
        </span>
      )}
    </div>
  );
}
